import React, { useEffect, useState, useRef, Fragment } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Upload, Eye, RotateCcw, CheckCircle2, XCircle, HelpCircle, MinusCircle, CircleDashed, Lightbulb, ShieldCheck, Users, Beaker, DollarSign, FolderOpen, UserCheck, Sparkles, ExternalLink, Pencil, X, MoreHorizontal, Check, Download, FileText, Loader2, ChevronDown } from 'lucide-react';
import { ArtifactCategoryData } from '../lib/types';
import type { ComplianceStatus, CriterionData } from '../lib/types';
const complianceStatusConfig = {
  satisfied: {
    icon: CheckCircle2,
    label: 'Satisfied',
    badgeCls: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border-0',
    iconCls: 'text-emerald-500'
  },
  'not-satisfied': {
    icon: XCircle,
    label: 'Not Satisfied',
    badgeCls: 'bg-red-50 text-red-700 hover:bg-red-50 border-0',
    iconCls: 'text-red-500'
  },
  unknown: {
    icon: HelpCircle,
    label: 'Unknown',
    badgeCls: 'bg-amber-50 text-amber-700 hover:bg-amber-50 border-0',
    iconCls: 'text-amber-500'
  },
  'not-checked': {
    icon: CircleDashed,
    label: 'Not Checked',
    badgeCls: 'bg-slate-50 text-slate-600 hover:bg-slate-50 border-0',
    iconCls: 'text-slate-400'
  },
  'not-applicable': {
    icon: MinusCircle,
    label: 'N/A',
    badgeCls: 'bg-slate-50 text-slate-500 hover:bg-slate-50 border-0',
    iconCls: 'text-slate-400'
  },
  'manual-review': {
    icon: UserCheck,
    label: 'Manual Check',
    badgeCls: 'bg-amber-50 text-amber-700 hover:bg-amber-50 border-0',
    iconCls: 'text-amber-500'
  }
};
const iconMap: Record<string, React.ElementType> = {
  Users,
  Beaker,
  DollarSign,
  FolderOpen
};
// Hardcoded NIH Formatting Checks
const nihFormattingChecks = [{
  label: 'Document is provided as a single PDF file.',
  passed: true
}, {
  label: 'Paper size is US Letter (8.5" × 11").',
  passed: true
}, {
  label: 'Margins are at least 0.5 inches on all sides.',
  passed: true
}, {
  label: 'No text, figures, or other content appears in the margins.',
  passed: true
}, {
  label: 'Font family is an NIH-accepted font (Arial, Helvetica, Palatino Linotype, or Georgia).',
  passed: true
}, {
  label: 'Font color is black.',
  passed: true
}, {
  label: 'Font size is 11 pt or larger (except figure labels that remain legible at 100% zoom).',
  passed: true
}, {
  label: 'Type density is no more than 15 characters per inch (including spaces).',
  passed: true
}, {
  label: 'Line spacing is no more than 6 lines per vertical inch.',
  passed: true
}, {
  label: 'No headers or footers are included in the uploaded PDF (NIH systems add them during assembly).',
  passed: true
}, {
  label: 'File name is ≤ 50 characters and uses only standard characters (letters, numbers, underscore, hyphen, space, period).',
  passed: true
}];
// Compute worst status for NIH formatting checks given whether artifact has a file
function getNihChecklistStatus(hasFile: boolean, overrides?: {
  label: string;
  passed: boolean;
}[]): {
  icon: React.ElementType;
  iconCls: string;
} {
  if (!hasFile) {
    return {
      icon: CircleDashed,
      iconCls: 'text-slate-300'
    };
  }
  const checks = overrides || nihFormattingChecks;
  const allPassed = checks.every(c => c.passed);
  const anyFailed = checks.some(c => !c.passed);
  if (anyFailed) {
    return {
      icon: XCircle,
      iconCls: 'text-red-400'
    };
  }
  if (allPassed) {
    return {
      icon: CheckCircle2,
      iconCls: 'text-emerald-500'
    };
  }
  return {
    icon: CircleDashed,
    iconCls: 'text-slate-400'
  };
}
interface ArtifactsTabProps {
  categories: ArtifactCategoryData[];
  criteria?: CriterionData[];
  onMarkReady?: (artifactId: string) => void;
  onUploadArtifact?: (artifactId: string) => void;
  uploadingArtifactId?: string | null;
  uploadedArtifactIds?: string[];
  isAdmin?: boolean;
  onExport?: () => void;
}
// Determine artifact owner: admin handles budget, submission, forms; PI handles research, personnel, supporting
function getArtifactOwner(categoryId: string, artifactId: string): {
  initials: string;
  name: string;
  color: string;
} {
  const admin = {
    initials: 'MS',
    name: 'Maria Santos',
    color: 'bg-emerald-100 text-emerald-700'
  };
  const pi = {
    initials: 'SC',
    name: 'Dr. Sarah Chen',
    color: 'bg-indigo-100 text-indigo-700'
  };
  // Budget category → admin
  if (categoryId === 'budget') return admin;
  // Submission & forms → admin
  if (categoryId === 'submission') return admin;
  // Specific admin-owned artifacts
  if (artifactId === 'current-pending') return admin;
  if (artifactId === 'A-01-SubmissionPackage') return admin;
  if (artifactId === 'A-09-BudgetJustification') return admin;
  if (artifactId === 'subcontract-budget') return admin;
  if (artifactId === 'consortium') return admin;
  if (artifactId === 'A-20-ConsortiumArrangements') return admin;
  if (artifactId === 'A-05-FacilitiesOtherResources') return pi;
  // Everything else → PI
  return pi;
}
export function ArtifactsTab({
  categories,
  criteria = [],
  onMarkReady,
  onUploadArtifact,
  uploadingArtifactId,
  uploadedArtifactIds = [],
  isAdmin,
  onExport
}: ArtifactsTabProps) {
  const [openConsiderationId, setOpenConsiderationId] = useState<string | null>(null);
  const [openMoreId, setOpenMoreId] = useState<string | null>(null);
  const [openNihFormatId, setOpenNihFormatId] = useState<string | null>(null);
  // Source Viewer State
  const [sourceViewerUrl, setSourceViewerUrl] = useState<string | null>(null);
  const [sourceViewerTitle, setSourceViewerTitle] = useState<string | null>(null);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  // Document Viewer Modal State
  const [docViewer, setDocViewer] = useState<{
    url: string;
    fileName: string;
  } | null>(null);
  // Demo document map — artifact ID → { url, fileName }
  const demoDocMap: Record<string, {
    url: string;
    fileName: string;
  }> = {
    'A-03-ProjectSummary': {
      url: 'https://cdn.magicpatterns.com/uploads/oDpaE3uJNpk71MqzvRuQZu/Untitled_3.png',
      fileName: 'Abstract_Chen_R21_RFA-EY-13-001_20260301.pdf'
    }
  };
  // Filter State
  const [filterRequired, setFilterRequired] = useState<'all' | 'Required' | 'Conditional'>('all');
  const [filterCheckStatus, setFilterCheckStatus] = useState<'all' | ComplianceStatus>('all');
  const [filterDocument, setFilterDocument] = useState<'all' | 'uploaded' | 'not-uploaded'>('all');
  const [filterOwner, setFilterOwner] = useState<'all' | 'SC' | 'MS'>('all');
  const [openFilterDropdown, setOpenFilterDropdown] = useState<string | null>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const considerationRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const nihFormatRef = useRef<HTMLDivElement>(null);
  const filterBarRef = useRef<HTMLDivElement>(null);
  const [filterBarHeight, setFilterBarHeight] = useState(0);
  useEffect(() => {
    if (!filterBarRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setFilterBarHeight(entry.contentRect.height + parseFloat(getComputedStyle(entry.target).paddingTop) * 2 + 1);
    });
    ro.observe(filterBarRef.current);
    // Set initial height
    const el = filterBarRef.current;
    setFilterBarHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (considerationRef.current && !considerationRef.current.contains(e.target as Node)) {
        setOpenConsiderationId(null);
      }
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setOpenMoreId(null);
      }
      if (nihFormatRef.current && !nihFormatRef.current.contains(e.target as Node)) {
        setOpenNihFormatId(null);
      }
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node)) {
        setOpenFilterDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Filter logic
  const hasActiveFilters = filterRequired !== 'all' || filterCheckStatus !== 'all' || filterDocument !== 'all' || filterOwner !== 'all';
  const filteredCategories = categories.map(category => {
    const filtered = category.artifacts.filter(artifact => {
      if (filterRequired !== 'all') {
        if (!artifact.requirementStatus || artifact.requirementStatus !== filterRequired) return false;
      }
      if (filterCheckStatus !== 'all') {
        if (artifact.complianceStatus !== filterCheckStatus) return false;
      }
      if (filterDocument !== 'all') {
        if (filterDocument === 'uploaded' && !artifact.hasFile) return false;
        if (filterDocument === 'not-uploaded' && artifact.hasFile) return false;
      }
      if (filterOwner !== 'all') {
        const owner = getArtifactOwner(category.id, artifact.id);
        if (owner.initials !== filterOwner) return false;
      }
      return true;
    });
    return {
      ...category,
      artifacts: filtered
    };
  }).filter(category => category.artifacts.length > 0);
  const clearFilters = () => {
    setFilterRequired('all');
    setFilterCheckStatus('all');
    setFilterDocument('all');
    setFilterOwner('all');
  };
  const openSourceViewer = (url: string, title: string = 'Source Document') => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  return <Card className="border-0 shadow-sm" data-id="element-48">
      <CardContent className="p-0" data-id="element-49">
        <div data-id="element-50">
          {isAdmin && onExport && <div className="px-4 py-3 flex justify-end border-b border-border bg-white rounded-t-lg" data-id="element-51">
              <Button size="sm" className="h-8 text-xs gap-1.5 bg-indigo-600 hover:bg-indigo-700" onClick={onExport} data-id="element-52">
                <Download className="h-3.5 w-3.5" data-id="element-53" />
                Export Packet
              </Button>
            </div>}

          {/* Filter Bar */}
          <div ref={filterBarRef} className="px-4 py-2.5 flex items-center gap-2 border-b border-border bg-white sticky top-0 z-20 flex-wrap shadow-[0_1px_0_0_hsl(var(--border))]" data-id="element-54">
            <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mr-1" data-id="element-55">
              Filters
            </span>

            {/* Required Filter */}
            <div className="relative" ref={openFilterDropdown === 'required' ? filterDropdownRef : undefined} data-id="element-56">
              <button onClick={() => setOpenFilterDropdown(openFilterDropdown === 'required' ? null : 'required')} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border transition-colors ${filterRequired !== 'all' ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' : 'bg-white text-foreground border-border hover:bg-muted/50'}`} data-id="element-57">
                Required?
                {filterRequired !== 'all' && <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1 rounded" data-id="element-58">
                    {filterRequired}
                  </span>}
                <ChevronDown className={`h-3 w-3 transition-transform ${openFilterDropdown === 'required' ? 'rotate-180' : ''}`} data-id="element-59" />
              </button>
              {openFilterDropdown === 'required' && <div className="absolute top-full left-0 mt-1 z-50 w-40 rounded-md bg-white border shadow-lg py-1" data-id="element-60">
                  <button onClick={() => {
                setFilterRequired('all');
                setOpenFilterDropdown(null);
              }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterRequired === 'all' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-foreground hover:bg-muted'}`} data-id="element-61">
                    All
                  </button>
                  <button onClick={() => {
                setFilterRequired('Required');
                setOpenFilterDropdown(null);
              }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterRequired === 'Required' ? 'bg-indigo-50 font-medium' : 'hover:bg-muted'}`} data-id="element-62">
                    <Badge className="text-[10px] px-1.5 py-0 h-4 border shadow-none font-medium bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-50" data-id="element-63">
                      Required
                    </Badge>
                  </button>
                  <button onClick={() => {
                setFilterRequired('Conditional');
                setOpenFilterDropdown(null);
              }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterRequired === 'Conditional' ? 'bg-amber-50/50 font-medium' : 'hover:bg-muted'}`} data-id="element-64">
                    <Badge className="text-[10px] px-1.5 py-0 h-4 border shadow-none font-medium bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50" data-id="element-65">
                      Conditional
                    </Badge>
                  </button>
                </div>}
            </div>

            {/* Check Status Filter */}
            <div className="relative" ref={openFilterDropdown === 'status' ? filterDropdownRef : undefined} data-id="element-66">
              <button onClick={() => setOpenFilterDropdown(openFilterDropdown === 'status' ? null : 'status')} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border transition-colors ${filterCheckStatus !== 'all' ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' : 'bg-white text-foreground border-border hover:bg-muted/50'}`} data-id="element-67">
                Check Status
                {filterCheckStatus !== 'all' && <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1 rounded" data-id="element-68">
                    {complianceStatusConfig[filterCheckStatus].label}
                  </span>}
                <ChevronDown className={`h-3 w-3 transition-transform ${openFilterDropdown === 'status' ? 'rotate-180' : ''}`} data-id="element-69" />
              </button>
              {openFilterDropdown === 'status' && <div className="absolute top-full left-0 mt-1 z-50 w-44 rounded-md bg-white border shadow-lg py-1" data-id="element-70">
                  <button onClick={() => {
                setFilterCheckStatus('all');
                setOpenFilterDropdown(null);
              }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterCheckStatus === 'all' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-foreground hover:bg-muted'}`} data-id="element-71">
                    All
                  </button>
                  {(Object.keys(complianceStatusConfig) as ComplianceStatus[]).map(key => {
                const cfg = complianceStatusConfig[key];
                const Icon = cfg.icon;
                return <button key={key} onClick={() => {
                  setFilterCheckStatus(key);
                  setOpenFilterDropdown(null);
                }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterCheckStatus === key ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-foreground hover:bg-muted'}`} data-id="element-72">
                        <Icon className={`h-3 w-3 ${cfg.iconCls}`} data-id="element-73" />
                        {cfg.label}
                      </button>;
              })}
                </div>}
            </div>

            {/* Document Filter */}
            <div className="relative" ref={openFilterDropdown === 'document' ? filterDropdownRef : undefined} data-id="element-74">
              <button onClick={() => setOpenFilterDropdown(openFilterDropdown === 'document' ? null : 'document')} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border transition-colors ${filterDocument !== 'all' ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' : 'bg-white text-foreground border-border hover:bg-muted/50'}`} data-id="element-75">
                Document
                {filterDocument !== 'all' && <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1 rounded" data-id="element-76">
                    {filterDocument === 'uploaded' ? 'Uploaded' : 'Missing'}
                  </span>}
                <ChevronDown className={`h-3 w-3 transition-transform ${openFilterDropdown === 'document' ? 'rotate-180' : ''}`} data-id="element-77" />
              </button>
              {openFilterDropdown === 'document' && <div className="absolute top-full left-0 mt-1 z-50 w-36 rounded-md bg-white border shadow-lg py-1" data-id="element-78">
                  {[{
                value: 'all' as const,
                label: 'All'
              }, {
                value: 'uploaded' as const,
                label: 'Uploaded'
              }, {
                value: 'not-uploaded' as const,
                label: 'Missing'
              }].map(opt => <button key={opt.value} onClick={() => {
                setFilterDocument(opt.value);
                setOpenFilterDropdown(null);
              }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterDocument === opt.value ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-foreground hover:bg-muted'}`} data-id="element-79">
                      {opt.label}
                    </button>)}
                </div>}
            </div>

            {/* Owner Filter */}
            <div className="relative" ref={openFilterDropdown === 'owner' ? filterDropdownRef : undefined} data-id="element-80">
              <button onClick={() => setOpenFilterDropdown(openFilterDropdown === 'owner' ? null : 'owner')} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border transition-colors ${filterOwner !== 'all' ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' : 'bg-white text-foreground border-border hover:bg-muted/50'}`} data-id="element-81">
                Owner
                {filterOwner !== 'all' && <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1 rounded" data-id="element-82">
                    {filterOwner === 'SC' ? 'PI' : 'Admin'}
                  </span>}
                <ChevronDown className={`h-3 w-3 transition-transform ${openFilterDropdown === 'owner' ? 'rotate-180' : ''}`} data-id="element-83" />
              </button>
              {openFilterDropdown === 'owner' && <div className="absolute top-full left-0 mt-1 z-50 w-48 rounded-md bg-white border shadow-lg py-1" data-id="element-84">
                  <button onClick={() => {
                setFilterOwner('all');
                setOpenFilterDropdown(null);
              }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterOwner === 'all' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-foreground hover:bg-muted'}`} data-id="element-85">
                    All
                  </button>
                  <button onClick={() => {
                setFilterOwner('SC');
                setOpenFilterDropdown(null);
              }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterOwner === 'SC' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-foreground hover:bg-muted'}`} data-id="element-86">
                    <div className="h-5 w-5 rounded-full flex items-center justify-center text-[8px] font-bold bg-indigo-100 text-indigo-700" data-id="element-87">
                      SC
                    </div>
                    Dr. Sarah Chen (PI)
                  </button>
                  <button onClick={() => {
                setFilterOwner('MS');
                setOpenFilterDropdown(null);
              }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterOwner === 'MS' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-foreground hover:bg-muted'}`} data-id="element-88">
                    <div className="h-5 w-5 rounded-full flex items-center justify-center text-[8px] font-bold bg-emerald-100 text-emerald-700" data-id="element-89">
                      MS
                    </div>
                    Maria Santos (Admin)
                  </button>
                </div>}
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && <button onClick={clearFilters} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors ml-1" data-id="element-90">
                <X className="h-3 w-3" data-id="element-91" />
                Clear
              </button>}
          </div>

          <table className="w-full text-sm" data-id="element-92">
            <thead className="sticky z-10" style={{
            top: `${filterBarHeight}px`
          }} data-id="element-93">
              <tr className="border-b bg-white shadow-[0_1px_0_0_hsl(var(--border))]" data-id="element-94">
                <th className="text-left font-medium text-muted-foreground uppercase tracking-wider text-[11px] px-4 py-2" data-id="element-95">
                  Artifact
                </th>
                <th className="text-left font-medium text-muted-foreground uppercase tracking-wider text-[11px] px-4 py-2 w-16" data-id="element-96">
                  Owner
                </th>
                <th className="text-left font-medium text-muted-foreground uppercase tracking-wider text-[11px] px-4 py-2 w-24" data-id="element-97">
                  Required?
                </th>
                <th className="text-left font-medium text-muted-foreground uppercase tracking-wider text-[11px] px-4 py-2 w-32" data-id="element-98">
                  Criteria
                </th>
                <th className="text-left font-medium text-muted-foreground uppercase tracking-wider text-[11px] px-4 py-2" data-id="element-99">
                  <div className="flex items-center gap-1.5" data-id="element-100">
                    <span data-id="element-101">Compliance Check</span>
                    <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-1 shadow-none normal-case tracking-normal" data-id="element-102">
                      <Sparkles className="h-3 w-3" data-id="element-103" />
                      AI Analysis
                    </Badge>
                  </div>
                </th>
                <th className="text-right font-medium text-muted-foreground uppercase tracking-wider text-[11px] px-4 py-2 w-28" data-id="element-104">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-105">
              {filteredCategories.map(category => {
              return <Fragment key={category.id} data-id="element-106">
                    {/* Artifact Rows */}
                    {category.artifacts.map(artifact => {
                  const config = complianceStatusConfig[artifact.complianceStatus];
                  const StatusIcon = config.icon;
                  const citationText = artifact.citation ? artifact.citation.replace(/^\[|\]$/g, '') : '';
                  // Get first source URL for the artifact title link
                  const firstSourceUrl = artifact.compliance?.find(c => c.sourceUrls && c.sourceUrls.length > 0)?.sourceUrls?.[0];
                  // Check if this artifact was uploaded (pre-analysis UI state only)
                  const isUploadedPreAnalysis = uploadedArtifactIds.includes(artifact.id);
                  return <tr key={artifact.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors align-top" data-id="element-107">
                          {/* Artifact Title + Source */}
                          <td className="px-4 py-3 max-w-[320px]" data-id="element-108">
                            <div className="font-medium text-foreground mb-1" data-id="element-109">
                              {artifact.title}
                            </div>
                            {citationText && <div className="flex items-center gap-1.5" data-id="element-110">
                                <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-1 shadow-none" data-id="element-111">
                                  <Sparkles className="h-3 w-3" data-id="element-112" />
                                  AI Extract
                                </Badge>
                                <button onClick={() => firstSourceUrl ? openSourceViewer(firstSourceUrl) : null} disabled={!firstSourceUrl} className={`inline-flex items-center gap-1 text-[10px] font-medium transition-colors ${firstSourceUrl ? 'text-violet-600 hover:text-violet-800 hover:underline cursor-pointer' : 'text-muted-foreground cursor-default'}`} data-id="element-113">
                                  <ExternalLink className="h-3 w-3 flex-shrink-0" data-id="element-114" />
                                  <span className="truncate max-w-[200px]" data-id="element-115">
                                    {citationText}
                                  </span>
                                </button>
                              </div>}
                          </td>

                          {/* Owner */}
                          <td className="px-4 py-3" data-id="element-116">
                            {(() => {
                        const owner = getArtifactOwner(category.id, artifact.id);
                        return <div className="flex items-center" title={owner.name} data-id="element-117">
                                  <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[9px] font-bold ${owner.color}`} data-id="element-118">
                                    {owner.initials}
                                  </div>
                                </div>;
                      })()}
                          </td>

                          {/* Required? */}
                          <td className="px-4 py-3" data-id="element-119">
                            {artifact.requirementStatus ? <Badge className={`text-[10px] px-1.5 py-0 h-5 border shadow-none font-medium ${artifact.requirementStatus === 'Required' ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-50' : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50'}`} data-id="element-120">
                                {artifact.requirementStatus}
                              </Badge> : <span className="text-xs text-muted-foreground" data-id="element-121">
                                —
                              </span>}
                          </td>

                          {/* Considerations */}
                          <td className="px-4 py-3" data-id="element-122">
                            {(() => {
                        // Find criteria that reference this artifact
                        const linkedCriteria = criteria.filter(c => {
                          const arts = Array.isArray(c.artifact) ? c.artifact : [c.artifact];
                          return arts.includes(artifact.id);
                        });
                        const count = linkedCriteria.length;
                        if (count > 0) {
                          return <div className="relative" ref={openConsiderationId === artifact.id ? considerationRef : undefined} data-id="element-123">
                                    <button onClick={() => setOpenConsiderationId(openConsiderationId === artifact.id ? null : artifact.id)} className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-2 py-1 rounded transition-colors" data-id="element-124">
                                      <Lightbulb className="h-3.5 w-3.5" data-id="element-125" />
                                      <span data-id="element-126">{count}</span>
                                    </button>
                                    {openConsiderationId === artifact.id && <div className="absolute top-full left-0 mt-1 z-50 w-80 rounded-lg bg-white border shadow-lg p-3" data-id="element-127">
                                        <div className="flex items-center justify-between mb-2" data-id="element-128">
                                          <div className="flex items-center gap-1.5" data-id="element-129">
                                            <Lightbulb className="h-3.5 w-3.5 text-indigo-600" data-id="element-130" />
                                            <span className="text-xs font-semibold text-indigo-700" data-id="element-131">
                                              Linked Criteria ({count})
                                            </span>
                                          </div>
                                          <button onClick={() => setOpenConsiderationId(null)} className="text-muted-foreground hover:text-foreground transition-colors" data-id="element-132">
                                            <X className="h-3.5 w-3.5" data-id="element-133" />
                                          </button>
                                        </div>
                                        <div className="space-y-2 max-h-48 overflow-y-auto pr-1" data-id="element-134">
                                          {linkedCriteria.map(c => <div key={c.id} className="rounded-md bg-indigo-50 border border-indigo-100 p-2" data-id="element-135">
                                              <p className="text-[11px] text-indigo-800 leading-snug" data-id="element-136">
                                                {c.title}
                                              </p>
                                            </div>)}
                                        </div>
                                      </div>}
                                  </div>;
                        }
                        return <span className="text-xs text-muted-foreground pl-2" data-id="element-137">
                                  —
                                </span>;
                      })()}
                          </td>

                          {/* AI Check */}
                          <td className="px-4 py-3" data-id="element-138">
                            <Badge className={`${config.badgeCls} text-[10px] px-1.5 py-0 h-5 gap-1 mb-2`} data-id="element-139">
                              <StatusIcon className="h-3 w-3" data-id="element-140" />
                              {config.label}
                            </Badge>

                            {artifact.compliance && artifact.compliance.length > 0 && <div className="space-y-1.5 pl-[7px]" data-id="element-141">
                                  {artifact.compliance.map((item, idx) => {
                          // Special handling for NIH Checklist Reference
                          if (item.isChecklistRef) {
                            const checksToUse = item.checklistOverrides || nihFormattingChecks;
                            const nihStatus = getNihChecklistStatus(!!artifact.hasFile, item.checklistOverrides);
                            const NihIcon = nihStatus.icon;
                            return <div key={idx} className="relative" ref={openNihFormatId === `${artifact.id}-${idx}` ? nihFormatRef : undefined} data-id="element-142">
                                          <button onClick={() => setOpenNihFormatId(openNihFormatId === `${artifact.id}-${idx}` ? null : `${artifact.id}-${idx}`)} className="inline-flex items-start gap-1.5 text-left transition-colors group" data-id="element-143">
                                            <NihIcon className={`h-3 w-3 mt-0.5 flex-shrink-0 ${nihStatus.iconCls}`} data-id="element-144" />
                                            <span className="text-[11px] leading-tight font-semibold text-sky-700 group-hover:text-sky-900 group-hover:underline" data-id="element-145">
                                              {item.label}
                                            </span>
                                          </button>

                                          {/* NIH Formatting Popover */}
                                          {openNihFormatId === `${artifact.id}-${idx}` && <div className="absolute top-full left-0 mt-1 z-50 w-80 rounded-lg bg-white border shadow-lg p-3" data-id="element-146">
                                              <div className="flex items-center justify-between mb-3 pb-2 border-b border-border" data-id="element-147">
                                                <div className="flex items-center gap-1.5" data-id="element-148">
                                                  <span className="text-xs font-semibold text-foreground" data-id="element-149">
                                                    NIH PDF Attachment
                                                    Formatting
                                                  </span>
                                                </div>
                                                <button onClick={() => setOpenNihFormatId(null)} className="text-muted-foreground hover:text-foreground transition-colors" data-id="element-150">
                                                  <X className="h-3.5 w-3.5" data-id="element-151" />
                                                </button>
                                              </div>
                                              <div className="space-y-2 max-h-60 overflow-y-auto pr-1" data-id="element-152">
                                                {checksToUse.map((check, i) => {
                                    const hasFileForCheck = !!artifact.hasFile;
                                    return <div key={i} className="flex items-start gap-2" data-id="element-153">
                                                      {!hasFileForCheck ? <CircleDashed className="h-3 w-3 text-slate-300 mt-0.5 flex-shrink-0" data-id="element-154" /> : check.passed ? <CheckCircle2 className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-155" /> : <XCircle className="h-3 w-3 text-red-400 mt-0.5 flex-shrink-0" data-id="element-156" />}
                                                      <span className={`text-[10px] leading-snug ${!hasFileForCheck ? 'text-muted-foreground' : check.passed ? 'text-muted-foreground' : 'text-red-600 font-medium'}`} data-id="element-157">
                                                        {check.label}
                                                      </span>
                                                    </div>;
                                  })}
                                              </div>
                                            </div>}
                                        </div>;
                          }
                          // Standard Check Item
                          return <div key={idx} className="flex items-start gap-1.5" data-id="element-158">
                                        {item.passed ? <CheckCircle2 className={`h-3 w-3 mt-0.5 flex-shrink-0 ${artifact.hasFile ? 'text-emerald-500' : 'text-slate-300'}`} data-id="element-159" /> : <XCircle className={`h-3 w-3 mt-0.5 flex-shrink-0 ${artifact.hasFile ? 'text-red-400' : 'text-slate-300'}`} data-id="element-160" />}
                                        <span className={`text-[11px] leading-tight ${!artifact.hasFile ? 'text-muted-foreground' : item.passed ? 'text-muted-foreground' : 'text-red-600 font-medium'}`} data-id="element-161">
                                          {item.label}
                                        </span>
                                      </div>;
                        })}
                                </div>}
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3" data-id="element-162">
                            <div className="flex items-center justify-end gap-1.5" data-id="element-163">
                              {artifact.readyStatus ? <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5 whitespace-nowrap border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800" onClick={() => onMarkReady?.(artifact.id)} data-id="element-164">
                                  <CheckCircle2 className="h-3 w-3" data-id="element-165" />
                                  Ready
                                </Button> : artifact.hasFile || isUploadedPreAnalysis ? <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5 whitespace-nowrap" onClick={() => onMarkReady?.(artifact.id)} data-id="element-166">
                                  <Check className="h-3 w-3" data-id="element-167" />
                                  Mark as Ready
                                </Button> : uploadingArtifactId === artifact.id ? <Button size="sm" className="h-7 text-xs gap-1.5 whitespace-nowrap bg-indigo-600 hover:bg-indigo-700" disabled data-id="element-168">
                                  <Loader2 className="h-3 w-3 animate-spin" data-id="element-169" />
                                  Uploading…
                                </Button> : <Button size="sm" className="h-7 text-xs gap-1.5 whitespace-nowrap bg-indigo-600 hover:bg-indigo-700" onClick={() => onUploadArtifact ? onUploadArtifact(artifact.id) : undefined} data-id="element-170">
                                  <Upload className="h-3 w-3" data-id="element-171" />
                                  Upload
                                </Button>}

                              {/* More dropdown */}
                              <div className="relative" ref={openMoreId === artifact.id ? moreRef : undefined} data-id="element-172">
                                <button onClick={() => setOpenMoreId(openMoreId === artifact.id ? null : artifact.id)} className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-border hover:bg-muted transition-colors" data-id="element-173">
                                  <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" data-id="element-174" />
                                </button>
                                {openMoreId === artifact.id && <div className="absolute top-full right-0 mt-1 z-50 w-36 rounded-md bg-white border shadow-lg py-1" data-id="element-175">
                                    {(artifact.hasFile || isUploadedPreAnalysis) && <button onClick={() => setOpenMoreId(null)} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors" data-id="element-176">
                                        <RotateCcw className="h-3 w-3" data-id="element-177" />
                                        Re-Upload
                                      </button>}
                                    <button onClick={() => {
                              setOpenMoreId(null);
                              const demoDoc = demoDocMap[artifact.id];
                              if (demoDoc) {
                                setDocViewer(demoDoc);
                              }
                            }} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors" data-id="element-178">
                                      <Eye className="h-3 w-3" data-id="element-179" />
                                      View
                                    </button>
                                    <button onClick={() => setOpenMoreId(null)} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors" data-id="element-180">
                                      <Pencil className="h-3 w-3" data-id="element-181" />
                                      Edit
                                    </button>
                                    <button onClick={() => setOpenMoreId(null)} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors" data-id="element-182">
                                      <X className="h-3 w-3" data-id="element-183" />
                                      Dismiss
                                    </button>
                                  </div>}
                              </div>
                            </div>
                          </td>
                        </tr>;
                })}
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>
      </CardContent>

      {/* Document Viewer Modal */}
      {docViewer && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setDocViewer(null)} data-id="element-184">
          <div className="relative bg-white rounded-lg shadow-2xl w-[90vw] max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-185">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-white rounded-t-lg" data-id="element-186">
              <div className="flex items-center gap-2 min-w-0" data-id="element-187">
                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" data-id="element-188" />
                <span className="text-sm font-medium text-foreground truncate" data-id="element-189">
                  {docViewer.fileName}
                </span>
              </div>
              <button onClick={() => setDocViewer(null)} className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-muted transition-colors flex-shrink-0" data-id="element-190">
                <X className="h-4 w-4 text-muted-foreground" data-id="element-191" />
              </button>
            </div>
            {/* Document Content */}
            <div className="flex-1 overflow-auto p-6 bg-slate-50" data-id="element-192">
              <img src={docViewer.url} alt={docViewer.fileName} className="w-full rounded-md border border-border shadow-sm bg-white" data-id="element-193" />
            </div>
          </div>
        </div>}
    </Card>;
}